import React from "react";
import { EWsrvTransform } from "../../lib/Media/util";
import { Carousel } from "../Carousel/Carousel";
import ListingCard, { EListingCardLayouts } from "./ListingCard";
import { ListingContact } from "./ListingContact";
import ListingDetails from "./ListingDetails";
import ListingMap from "./ListingMap";
import { ListingSummary } from "./ListingSummary";
import { EListingVitalsLayout, ListingVitals } from "./ListingVitals";
import { IListingLayoutProps } from "./util";
import { ContactForm } from "../Forms";
import { BackgroundImage } from "../Media";
import TabbedContentBlade, { ITabContent } from "../Blades/TabbedContentBlade";
import { IListing } from "../../lib";
import { WsrvImage } from "../Media";
import ListingDocuments from "./ListingDocuments";
import { ListingOpenHouse } from "./ListingOpenHouse";
import { ListingShare } from "./ListingShare";

// Simplified image optimization props
export interface IImageOptimizationProps {
    // Performance props (NOTE: Currently ALL images eagerly load and preload)
    eagerLoadCount?: number; // Legacy: Number of images to load with high priority (currently all images load)
    preloadCount?: number; // Legacy: Number of adjacent images to preload (currently all images preload)
    imageQuality?: number; // Image quality (1-100)
    preloadAdjacent?: boolean; // Legacy: Whether to preload adjacent images (currently all images preload)
    outputFormat?: 'jpg' | 'webp' | 'avif' | 'png'; // Image output format (defaults to webp)
}

// Extended interface with optimization props
export interface IListingLayoutPropsWithOptimization extends IListingLayoutProps, IImageOptimizationProps {}

// Add this helper function after imports
const isValidVideoUrl = (url: string) => {
  // Allow only known video providers or direct video files
  return /(youtube\.com|youtu\.be|vimeo\.com|player\.vimeo\.com|dailymotion\.com|wistia\.com|\.mp4$|\.webm$|\.ogg$)/i.test(url);
};

export const ListingLayoutSingleColumnAlt: React.FC<IListingLayoutPropsWithOptimization> = ({
    listing,
    title,
    linkFunc,
    similar,
    agents,
    className = "",
    similarCardProps,
    similarCarouselProps,
    detailsProps,
    vitalsProps,
    contactProps,
    formProps,
    summaryProps,
    mapProps,
    rowClassName = "",
    showContactUsForm,
    contactUsFormProps,
    documentsProps,
    photosTabTitle,
    propertyVideoTabTitle,
    virtualTourTabTitle,
    floorplansTabTitle,
    displayDisclaimer = false,
    disclaimerCssClasses = "",
    disclaimers,
    galleryCarouselProps,
    listingShareProps,
    heroWrapperClassName,
    carouselProps,
    // Image optimization props
    eagerLoadCount = 15,
    preloadCount = 2,
    imageQuality = 85,
    preloadAdjacent = true,
    outputFormat = 'webp'
}) => {

    const MediaSection: React.FC<IListing> = (listing) => {
        const galleryCarousel = (
            <Carousel
                className={`slick-center-zoom`}
                {...galleryCarouselProps}
                slickSettings={{
                    infinite: true,
                    centerMode: true,
                    slidesToShow: 1,
                    arrows: true,
                    slidesToScroll: 1,
                    adaptiveHeight: false,
                    ...galleryCarouselProps?.slickSettings,
                }}
            >
                {listing.images?.map((image, index) => {
                    return (
                        <WsrvImage
                            key={index}
                            urlParams={{
                                originURL: image.url,
                                preset: EWsrvTransform.cover4x3,
                                width: 1280
                            }}
                            widthMax={1280}
                            priority="high"
                            eagerLoad={true}
                            preload={true}
                            optimizationOptions={{
                                quality: imageQuality,
                                background: 'white',
                                output: outputFormat
                            }}
                        />
                    )
                })}
            </Carousel>
        )

        if (listing.videos || listing.tours || listing.floorplans) {
            let tabContent: ITabContent[] = [
                {
                    tabTitle: photosTabTitle ?? "Photos",
                    contentClassName: "w-full",
                    content: galleryCarousel,
                },
            ];
            if (listing.videos && listing.videos.length > 0 && isValidVideoUrl(listing.videos[0].url)) {
                tabContent.push({
                    tabTitle: propertyVideoTabTitle ?? "Property Video",
                    contentClassName: "max-w-screen-xl w-full",
                    content: (
                        <iframe
                            src={listing.videos[0].url}
                            allowFullScreen
                            allow="encrypted-media"
                            className="w-full aspect-video"
                        />
                    ),
                });
            }
            if (listing.tours && listing.tours.length > 0) {
                tabContent.push({
                    tabTitle: virtualTourTabTitle ?? "Virtual Tour",
                    contentClassName: "max-w-screen-xl w-full",
                    content: (
                        <iframe
                            src={listing.tours[0].url}
                            className="w-full aspect-video"
                        />
                    ),
                });
            }
            if (listing.floorplans && listing.floorplans.length > 0) {
                tabContent.push({
                    tabTitle: floorplansTabTitle ?? "Floor Plans",
                    content: (
                        <>
                            {listing.floorplans.map((plan, index) => (
                                <img src={plan.url} key={index} />
                            ))}
                        </>
                    ),
                });
            }
            return (
                <TabbedContentBlade
                    title="Media"
                    className="w-full mx-auto"
                    tabListClassName="inverted"
                    tabs={tabContent}
                />
            );
        } else {
            return (
                <>
                    {galleryCarousel}
                </>
            );
        }
    };

    return (
        <div className={`${className} listing-layout layout-single-column-alt`}>
            {listing.images &&
                (carouselProps == null ?
                    <BackgroundImage src={listing.images[0].url}>
                        <div
                            className={`relative w-full h-screen flex flex-col items-center justify-end mb-10 bg-gradient-to-t from-black/80 via-transparent to-transparent ${heroWrapperClassName}`}
                        >
                            <ListingVitals listing={listing} {...vitalsProps} />
                        </div>
                    </BackgroundImage>
                    :
                    <div className={`relative w-full md:h-screen mb-10 ${heroWrapperClassName}`}>
                        <Carousel
                            className="w-full md:h-screen"
                            {...carouselProps}
                            slickSettings={{
                                ...carouselProps?.slickSettings,
                            }}
                        >
                            {listing.images?.map((img, index) => (
                                <WsrvImage                                    
                                    urlParams={{
                                        originURL: img.url,
                                        preset: EWsrvTransform.cover16x9,
                                        width: 1280,
                                    }}
                                    key={index}
                                    widthMax={1280}
                                    priority="high"
                                    eagerLoad={true}
                                    preload={true}
                                    optimizationOptions={{
                                        quality: imageQuality,
                                        background: 'white',
                                        output: outputFormat
                                    }}
                                    imgProps={{
                                        className: "w-full md:h-screen mx-auto object-cover",
                                        width: img.dimensions?.[0],
                                        height: img.dimensions?.[1],
                                        wrapperClassName: "[&_>_span]:!w-full [&_>_span]:!h-auto",
                                        wrapperProps: {
                                            style: {},
                                        },
                                        alt: img.caption || listing.address.text,
                                    }}
                                />
                            ))}
                        </Carousel>
                        <div className="absolute right-0 bottom-0 left-0 flex flex-col items-center justify-end bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                            <ListingVitals listing={listing} {...vitalsProps} />
                        </div>
                    </div>
                )
            }
            <div className={`px-5 md:grid md:grid-cols-2 md:space-x-5 ${rowClassName}`}>
                {title && <h1 className="md:col-span-2 text-center">{title}</h1>}
                <div className="mb-3">
                    <ListingSummary listing={listing} {...summaryProps} />
                    <ListingShare className="mt-12" listingPageUrl={listing.path ?? ""} {...listingShareProps} />
                </div>
                <div className={`mb-3`}>
                    <ListingOpenHouse openHouse={listing.openHouse} className="mb-3" />
                    <ContactForm {...formProps}>
                        <ListingContact
                            listing={listing}
                            agents={agents}
                            offices={listing.office}
                            linkFunc={linkFunc}
                            {...contactProps}
                        />
                    </ContactForm>
                </div>
            </div>
            {listing.propertyDocuments && listing.propertyDocuments.length > 0 && (
                <ListingDocuments documents={listing.propertyDocuments} {...documentsProps} />
            )}
            <MediaSection {...listing} />
            {listing.details && listing.details.length > 0 && (
                <ListingDetails
                    detailGroups={listing.details}
                    className="py-3"
                    {...detailsProps}
                />
            )}
            {listing.location && listing.location?.lat && listing.location.lng && (
                <ListingMap listing={listing} title="Location" {...mapProps} />
            )}
            {similar && similar.length > 2 && (
                <Carousel
                    title="Similar"
                    slickSettings={{
                        infinite: true,
                        centerMode: true,
                        centerPadding: "10px",
                        slidesToShow: Math.min(3, similar.length),
                        arrows: true,
                        ...similarCarouselProps?.slickSettings,
                    }}
                    className={`slick-center-zoom ${similarCarouselProps?.className || ""
                        }`}
                    {...similarCarouselProps}
                >
                    {similar.map((listing, index) => {
                        return (
                            <ListingCard
                                layout={EListingCardLayouts.vertical}
                                vitalsLayout={EListingVitalsLayout.column2Border1}
                                listing={listing}
                                className="max-w-[680px] mx-auto"
                                maxWidthImage={680}
                                key={index}
                                linkFunc={linkFunc}
                                {...similarCardProps}
                            />
                        );
                    })}
                </Carousel>
            )}
            {showContactUsForm && <ContactForm {...contactUsFormProps} />}
            {displayDisclaimer && (
                <section className={disclaimerCssClasses} dangerouslySetInnerHTML={{ __html: disclaimers?.find(x => x.mlsSourceName === listing.mlsSourceName)?.disclaimerText ?? "" }}>
                </section>
            )}
        </div>
    );
};